/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global DockableSideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends DockableSideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.checkoutIntent = false;
      this.cartMutationSequence = 0;
      this.activeCartMutation = 0;
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
      document.removeEventListener('aonsk:checkout-intent', this.checkoutIntentHandler);
      document.removeEventListener('aonsk:checkout-intent-clear', this.checkoutIntentClearHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          if (!this.isDocked) {
            evt.preventDefault();
            this.open(cartIcon);
            this.refreshCartDrawer();
          }
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (!this.isDocked) {
            if (evt.key !== ' ') return;
            evt.preventDefault();
            this.open(cartIcon);
            this.refreshCartDrawer();
          }
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      this.addEventListener('click', this.handleEmptyCart.bind(this));

      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      this.checkoutIntentHandler = () => {
        this.checkoutIntent = true;
        this.setAttribute('data-checkout-intent', 'true');
      };
      this.checkoutIntentClearHandler = () => {
        this.checkoutIntent = false;
        this.removeAttribute('data-checkout-intent');
      };
      document.addEventListener('aonsk:checkout-intent', this.checkoutIntentHandler);
      document.addEventListener('aonsk:checkout-intent-clear', this.checkoutIntentClearHandler);

      this.refreshHandler = () => this.refreshCartDrawer();
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Clears the cart
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!this.isDocked && evt.target.closest('.js-close-drawer')) {
        evt.preventDefault();
        this.close();
        return;
      }
      super.handleClick(evt);
    }

    handleEmptyCart(evt) {
      if (!evt.target.matches('.js-empty-cart')) return;
      evt.preventDefault();
      return window.aonskCartQueue.run(() => this.emptyCart(evt));
    }

    async emptyCart(evt) {
      if (evt.target.matches('.js-empty-cart')) {
        evt.preventDefault();

        // eslint-disable-next-line no-alert, no-restricted-globals
        if (confirm(theme.strings.confirmEmptyCart)) {
          const emptyCartLink = evt.target;
          emptyCartLink.setAttribute('aria-busy', 'true');
          emptyCartLink.classList.add('is-loading');

          const cartItems = Array.from(this.querySelectorAll('.cart-item'));
          cartItems.forEach((cartItem, index) => {
            setTimeout(() => cartItem.classList.add('cart-item--out'), index * 50);
          });

          try {
            const response = await fetch(`${theme.routes.cartClear}.js`, {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              cache: 'no-store'
            });

            if (!response.ok) throw new Error(response.status);

            // Broadcast individual events to update product cards and quick-add quantities.
            cartItems.forEach((cartItem) => {
              this.dispatchEvent(new CustomEvent('on:line-item:change', {
                bubbles: true,
                detail: {
                  variantId: cartItem.dataset.variantId,
                  newQuantity: 0
                }
              }));
            });

            // Pull fresh cart sections after the clear request. Force a full drawer refresh so stale
            // cart-items markup cannot keep old line items visible and create a clear-cart loop.
            await this.readCartDrawer(true);
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          } catch (error) {
            console.log('Error clearing cart:', error); // eslint-disable-line
            // Last-resort fallback uses Shopify's native clear URL so shoppers are never trapped.
            window.location.href = `${theme.routes.cartClear}?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`;
          } finally {
            emptyCartLink.removeAttribute('aria-busy');
            emptyCartLink.classList.remove('is-loading');
          }
        }
      }
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      this.cartSummary = this.querySelector('.cart-drawer__summary');
      if (this.hasAttribute('open')) return;
      this.checkoutIntent = false;
      this.removeAttribute('data-checkout-intent');
      document.documentElement.classList.add('bcm6-cart-drawer-open');
      document.body.classList.add('bcm6-cart-drawer-open');

      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      window.clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => {
        super.open(opener || this.opener, elementToFocus, () => {
          if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
    }

    /**
     * Closes the drawer and restores page-level floating widgets.
     * @param {Element} [returnFocus] - Element to focus after the drawer closes.
     */
    close(returnFocus) {
      window.clearTimeout(this.openTimer);
      document.documentElement.classList.remove('bcm6-cart-drawer-open');
      document.body.classList.remove('bcm6-cart-drawer-open');
      super.close(returnFocus);
    }

    /**
     * Starts a cart mutation and temporarily disables checkout until the drawer reflects the
     * updated cart. A monotonically increasing token prevents older network responses from
     * overwriting newer cart state.
     * @param {string} source - Human-readable mutation source for diagnostics.
     * @returns {number}
     */
    beginCartMutation(source = 'cart') {
      const token = ++this.cartMutationSequence;
      this.activeCartMutation = token;
      this.setCartUpdating(true, source);
      window.__AONSK_CHECKOUT_DIAGNOSTICS__ = window.__AONSK_CHECKOUT_DIAGNOSTICS__ || {};
      window.__AONSK_CHECKOUT_DIAGNOSTICS__.cartMutation = { token, source, startedAt: Date.now() };
      return token;
    }

    /**
     * Whether a response still belongs to the newest cart mutation and checkout has not begun.
     * @param {number|null} token - Mutation token.
     * @returns {boolean}
     */
    isCartMutationCurrent(token) {
      if (this.checkoutIntent || window.__aonskCheckoutIntentActive) return false;
      return token === null || token === undefined || token === this.activeCartMutation;
    }

    /**
     * Completes a mutation without allowing an older request to re-enable controls belonging to
     * a newer request.
     * @param {number|null} token - Mutation token.
     */
    endCartMutation(token) {
      if (token !== null && token !== undefined && token !== this.activeCartMutation) return;
      this.setCartUpdating(false);
    }

    /**
     * Gives shoppers a clear, temporary state while Shopify updates quantities. Checkout is
     * restored in finally blocks even when a request fails or times out.
     * @param {boolean} updating - Whether a cart mutation is pending.
     * @param {string} source - Mutation source.
     */
    setCartUpdating(updating, source = '') {
      this.toggleAttribute('data-cart-update-pending', updating);
      if (updating) this.setAttribute('aria-busy', 'true');

      this.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((control) => {
        if (updating) {
          if (!control.hasAttribute('data-aonsk-was-disabled')) {
            control.setAttribute('data-aonsk-was-disabled', control.disabled ? 'true' : 'false');
            control.setAttribute('data-aonsk-original-html', control.innerHTML || control.value || 'Checkout');
          }
          if (!control.disabled) {
            control.disabled = true;
            control.setAttribute('aria-busy', 'true');
            if (control.tagName === 'BUTTON') control.textContent = 'Updating cart…';
          }
        } else {
          const wasDisabled = control.getAttribute('data-aonsk-was-disabled');
          const original = control.getAttribute('data-aonsk-original-html');
          if (wasDisabled === 'false') control.disabled = false;
          control.removeAttribute('aria-busy');
          if (original && control.tagName === 'BUTTON') control.innerHTML = original;
          control.removeAttribute('data-aonsk-was-disabled');
          control.removeAttribute('data-aonsk-original-html');
        }
      });

      this.querySelectorAll('[data-aonsk-cart-update-status]').forEach((status) => {
        status.textContent = updating
          ? 'Updating your cart. Checkout will be available in a moment.'
          : 'Cart updated. Checkout is available.';
      });

      if (!updating) this.removeAttribute('aria-busy');
      if (updating) this.dataset.cartUpdateSource = source;
      else delete this.dataset.cartUpdateSource;
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    refreshCartDrawer(dontRefreshCartItems = false) {
      this.fullRefreshRequested = this.fullRefreshRequested || dontRefreshCartItems === true;
      if (this.refreshPromise) return this.refreshPromise;
      this.refreshPromise = window.aonskCartQueue.run(async () => {
        const full = this.fullRefreshRequested;
        this.fullRefreshRequested = false;
        return this.readCartDrawer(full);
      }).finally(() => { this.refreshPromise = null; });
      return this.refreshPromise;
    }

    async readCartDrawer() {
      const mutationToken = this.beginCartMutation('drawer-refresh');
      this.removeAttribute('data-cart-refresh-failed');
      try {
        const sectionIds = [...new Set(this.getSectionsToRender().map((section) => section.section))].slice(0, 5);
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionIds.join(','));
        url.searchParams.set('_aonsk_cart', Date.now().toString());
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);
        let response;
        try {
          response = await fetch(url.toString(), { cache: 'no-store', signal: controller.signal });
        } finally {
          window.clearTimeout(timeout);
        }
        if (!response.ok) throw new Error(response.status);
        const sections = await response.json();
        if (!this.isCartMutationCurrent(mutationToken)) return false;
        if (!await this.renderContents({ sections }, false, mutationToken)) {
          throw new Error('Cart sections could not be rendered');
        }
        return true;
      } catch (error) {
        if (this.isCartMutationCurrent(mutationToken)) this.setAttribute('data-cart-refresh-failed', 'true');
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error.message } }));
        return false;
      } finally {
        this.endCartMutation(mutationToken);
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true, mutationToken = null) {
      if (!this.isCartMutationCurrent(mutationToken)) return false;
      if (!data || !data.sections || typeof data.sections !== 'object') return false;
      const drawerSection = this.getSectionsToRender().find((section) => section.id === 'cart-drawer');
      const drawerHtml = data.sections[drawerSection.section];
      if (typeof drawerHtml !== 'string') return false;
      const drawerTemplate = document.createElement('template');
      drawerTemplate.innerHTML = drawerHtml;
      const nextDrawer = drawerTemplate.content.querySelector('cart-drawer');
      if (!nextDrawer || !nextDrawer.querySelector('cart-items')) return false;
      const nextCount = Number(nextDrawer.dataset.cartCount);
      if (!Number.isFinite(nextCount)) return false;
      // A successful write can still contain a null or stale section. Never replace a
      // confirmed add with an empty drawer, or a quantity change with a different total.
      if (typeof data.item_count === 'number' && nextCount !== data.item_count) return false;
      if (data.variant_id && !Array.from(nextDrawer.querySelectorAll('.cart-item'))
        .some((item) => item.dataset.variantId === String(data.variant_id))) return false;
      if (nextCount > 0 && !nextDrawer.querySelector('.cart-item')) return false;

      // Optional enhancements must not delay or prevent a confirmed cart from displaying.
      this.loadOptionalScripts();

      if (!this.isCartMutationCurrent(mutationToken)) return false;
      this.dataset.cartEmpty = nextDrawer.dataset.cartEmpty;
      this.dataset.cartCount = nextDrawer.dataset.cartCount;
      this.removeAttribute('data-cart-refresh-failed');

      this.getSectionsToRender().forEach((section) => {
        if (!this.isCartMutationCurrent(mutationToken)) return;
        const sectionHtml = data.sections[section.section];
        if (typeof sectionHtml !== 'string') return;

        const el = document.getElementById(section.id);
        if (!el) return;

        const replacement = CartDrawer.getElementHTML(sectionHtml, section.selector);
        if (replacement === null) return;
        el.innerHTML = replacement;

        // Update the cart drawer quantity
        if (section.id === 'cart-icon-bubble') {
          const drawerCount = document.getElementById('cart-drawer__count');
          if (drawerCount) {
            const html = CartDrawer.getElementHTML(
              sectionHtml,
              `${section.selector} .header__cart-count-number`
            );
            if (html !== null) drawerCount.innerHTML = `(${html})`;
          }
        }
      });

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      if (this.hasAttribute('open') && !this.isDocked) trapFocus(this);

      if (openDrawer && this.getAttribute('open') === null) {
        this.open(this.opener);
      }

      window.initLazyImages();
      CartDrawer.recalculateCssVarHeights();
      return true;
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    loadOptionalScripts() {
      if (this.dataset.shippingCalculator !== 'true' || this.optionalScriptsPromise) return;
      this.optionalScriptsPromise = loadScript(theme.scripts.countryProvinceSelector)
        .then(() => loadScript(theme.scripts.shippingCalculator))
        .catch(() => { this.optionalScriptsPromise = null; });
    }

    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];

      // Append any quick order lists to the sections
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections.push({
            id: `quick-order-list_${sectionId}`,
            section: sectionId,
            selector: `#quick-order-list_${sectionId}`
          });
        }
      });

      return sections;
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      if (typeof html !== 'string') return null;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : null;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}


